const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

export const DEMO_ROLE_IDS = new Set([3, 4]);

export function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

export function isDemoMode() {
  const user = getStoredUser();
  return DEMO_ROLE_IDS.has(Number(user?.role_id));
}

export function getFallbackSectionId(sectionId) {
  if (sectionId) return Number(sectionId);

  const user = getStoredUser();

  return user?.section_id ? Number(user.section_id) : null;
}

export function getAuthorId() {
  const user = getStoredUser();
  return user?.id || user?.user_id || 1;
}

export function isExternalURL(url) {
  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch {
    return false;
  }
}

export function resolveMediaUrl(url, fallback = "/images/asso_placeholder.webp") {
  if (!url) return fallback;

  const value = String(url).trim();

  if (
    isExternalURL(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

export function normalizeText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function stripHtml(value = "") {
  if (!value) return "";

  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", max = 130) {
  const clean = stripHtml(value);

  if (clean.length <= max) return clean;

  return `${clean.slice(0, max).trim()}...`;
}

export function getNewsDate(article) {
  return article?.created_at || article?.createdAt || article?.date || null;
}

export function formatDate(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function sortNewsByDate(news = []) {
  return [...news].sort((a, b) => {
    const dateA = new Date(getNewsDate(a) || 0).getTime();
    const dateB = new Date(getNewsDate(b) || 0).getTime();

    return dateB - dateA;
  });
}

export function filterNews(news = [], searchTerm = "", statusFilter = "all") {
  const q = normalizeText(searchTerm.trim());

  return news.filter((article) => {
    const isActive = Boolean(article?.is_active);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && isActive) ||
      (statusFilter === "inactive" && !isActive);

    const searchable = normalizeText(
      [
        article?.title,
        stripHtml(article?.content),
        article?.section?.nom_section,
        article?.author?.prenom,
        article?.author?.nom,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchesSearch = !q || searchable.includes(q);

    return matchesStatus && matchesSearch;
  });
}

export function getNewsStats(news = []) {
  const total = news.length;
  const active = news.filter((article) => article.is_active).length;
  const inactive = total - active;
  const latest = sortNewsByDate(news)[0];

  return {
    total,
    active,
    inactive,
    latestDate: latest ? getNewsDate(latest) : null,
  };
}

export const emptyNewsForm = {
  title: "",
  content: "",
  image_url: "",
  is_active: false,
};

export function mapArticleToForm(article) {
  return {
    title: article?.title || "",
    content: article?.content || "",
    image_url: article?.image_url || "",
    is_active: Boolean(article?.is_active),
  };
}